import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { slotKey, rankSlotMatches, parseSlotRanges, slotSearchText, sortByNumber, collapseToRanges, namesBetween } from '../../lib/slotSelection';
import { togglePick, looksLikeRange } from '../../lib/slotPick';

/*
 * r16q10: which slots the Inbox shows. The cached ['slots'] list, searched like the Slots page (slot number, name, label, @account), hundreds of them to scroll through.
 *   click a slot            only that slot
 *   tick it, or Ctrl+click  several: the list stays open
 *   Shift+click             every slot between the last one ticked and this one
 *   type 801-830, 905       a range: Enter or the line under the box adds them
 *   Pick all                every slot the search found
 */
export const SLOT_LIST_MAX = 500;

export default function SlotFilter({ slots, picked, onPick }) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const lastTick = useRef(null);

  const rows = useMemo(() => {
    const all = (slots || []).map((s) => ({ name: slotKey(s), slot: s, searchText: slotSearchText(s) })).filter((r) => r.name);
    const order = new Map(sortByNumber(all.map((r) => r.name)).map((n, i) => [n, i]));
    return all.sort((a, b) => order.get(a.name) - order.get(b.name));
  }, [slots]);
  const names = useMemo(() => rows.map((r) => r.name), [rows]);
  const matches = useMemo(() => (open ? rankSlotMatches(rows, text, SLOT_LIST_MAX) : { rows: [], total: 0 }), [rows, text, open]);
  const parsed = useMemo(() => (open && looksLikeRange(text) ? parseSlotRanges(text, names) : null), [open, text, names]);
  const pickedSet = useMemo(() => new Set(picked), [picked]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const close = () => { setText(''); setOpen(false); };
  const only = (name) => { lastTick.current = name; close(); onPick([name]); };
  const toggle = (name) => { lastTick.current = name; onPick(togglePick(picked, name)); };
  const addMany = (list) => onPick([...new Set([...picked, ...list])]);
  const addRange = () => { addMany(parsed.names); setText(''); };
  const run = (name) => {
    const between = lastTick.current ? namesBetween(names, lastTick.current, name) : [name];
    lastTick.current = name;
    addMany(between);
  };
  const pickAll = () => addMany(rankSlotMatches(rows, text).rows.map((r) => r.name));
  const clickRow = (e, name) => {
    if (e.ctrlKey || e.metaKey) toggle(name);
    else if (e.shiftKey) run(name);
    else only(name);
  };
  const submit = (e) => {
    e.preventDefault();
    if (parsed && parsed.names.length) addRange();
    else if (matches.rows.length) only(matches.rows[0].name);
  };

  const input = (
    <form onSubmit={submit} className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <input value={text} onChange={(e) => { setText(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape') close(); }}
        placeholder={picked.length ? 'Search, or 801-830, 905' : 'All slots · search or 801-830'} aria-label="Pick slots" autoComplete="off" autoFocus={picked.length > 0} data-testid="inbox-slot-input"
        className="h-9 w-full rounded-full border border-white/10 bg-secondary/60 pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground" />
    </form>
  );

  return (
    <div className="relative" ref={box} data-testid="inbox-slot-picker">
      {picked.length === 0 ? (
        <div className="w-52 sm:w-60">{input}</div>
      ) : (
        <span className="inline-flex h-9 items-center gap-1 rounded-full border border-primary/30 bg-primary/10 pl-3 pr-1 text-sm text-primary">
          <button type="button" className="font-mono" onClick={() => setOpen((v) => !v)} title={collapseToRanges(picked)} aria-expanded={open} data-testid="inbox-slot-current">
            {picked.length === 1 ? picked[0] : `${picked.length} slots`}
          </button>
          <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded-full hover:bg-primary/20" onClick={() => { close(); onPick([]); }}
            aria-label="Show all slots" data-testid="inbox-slot-clear">
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </span>
      )}
      {open && (
        <div className="absolute left-0 top-10 z-30 flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-1.5 rounded-xl border border-border bg-popover p-2 shadow-xl" data-testid="inbox-slot-panel">
          {picked.length > 0 && input}
          {parsed && parsed.names.length > 0 && (
            <button type="button" onClick={addRange} className="tap rounded-lg border border-primary/30 bg-primary/10 px-2.5 py-1.5 text-left text-xs text-primary" data-testid="inbox-slot-range">
              Pick {parsed.names.length} {parsed.names.length === 1 ? 'slot' : 'slots'}: {collapseToRanges(parsed.names)}
              {parsed.unknown.length > 0 && <span className="block text-muted-foreground">{parsed.unknown.length + parsed.unknownOverflow} of those aren&apos;t on this account</span>}
            </button>
          )}
          {parsed && parsed.names.length === 0 && (
            <p className="px-2.5 py-1 text-xs text-muted-foreground" data-testid="inbox-slot-range-none">None of those slots are on this account.</p>
          )}
          <div className="flex items-center gap-2 px-1 text-[11px] text-muted-foreground">
            <span className="min-w-0 flex-1 truncate" data-testid="inbox-slot-count">
              {picked.length ? `${picked.length} picked: ${collapseToRanges(picked, 3)}` : `${matches.total} slots · tick or Ctrl+click for several`}
            </span>
            {matches.total > 1 && (
              <button type="button" onClick={pickAll} className="shrink-0 font-medium text-primary hover:underline" data-testid="inbox-slot-pick-all">Pick all {matches.total}</button>
            )}
            {picked.length > 0 && (
              <button type="button" onClick={() => onPick([])} className="shrink-0 font-medium text-primary hover:underline" data-testid="inbox-slot-none">Clear</button>
            )}
          </div>
          <ul className="max-h-80 overflow-y-auto" role="listbox" aria-multiselectable="true" data-testid="inbox-slot-list">
            {matches.rows.map((r) => (
              <li key={r.name} className={cn('flex min-w-0 items-center gap-2 rounded-lg pl-2.5 hover:bg-secondary/70', pickedSet.has(r.name) && 'bg-primary/10')}>
                <input type="checkbox" className="h-3.5 w-3.5 shrink-0 accent-primary" checked={pickedSet.has(r.name)} onChange={() => toggle(r.name)} aria-label={`Pick ${r.name}`} data-testid={`inbox-slot-tick-${r.name}`} />
                <button type="button" role="option" aria-selected={pickedSet.has(r.name)} onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }} onClick={(e) => clickRow(e, r.name)}
                  className="flex min-w-0 flex-1 items-center gap-2 py-2 pr-2.5 text-left text-sm" data-testid={`inbox-slot-option-${r.name}`}>
                  <span className="font-mono text-primary">{r.name}</span>
                  {r.slot && (r.slot.slot_label || r.slot.ig_username) && <span className="min-w-0 truncate text-xs text-muted-foreground">{r.slot.slot_label || `@${r.slot.ig_username}`}</span>}
                </button>
              </li>
            ))}
            {matches.total === 0 && <li className="px-2.5 py-2 text-xs text-muted-foreground">No slot matches that.</li>}
            {matches.total > matches.rows.length && <li className="px-2.5 py-1.5 text-[11px] text-muted-foreground" data-testid="inbox-slot-more">{matches.total - matches.rows.length} more: keep typing to find them</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
